import shoelaceStyles from '../styles/shoelaceStyles';
import styles from '../styles/styles.scss';
import smartmapsAutocompleteStyles from '@smartmaps/autocomplete/dist/smartmaps-autocomplete.css';
import {css, html, LitElement} from 'lit';
import {customElement} from 'lit/decorators.js';
import {ConfigurationState} from '../storage/ConfigurationState';
import {createRef, Ref, ref} from 'lit/directives/ref.js';
import {createAutocomplete} from '@smartmaps/autocomplete';
import {log} from '../controllers/LogController';
import {SearchController} from '../controllers/SearchController';
import {Feature, Point} from 'geojson';

// prevent tree shaking
export {};

@customElement('ca-form')
export class CaFormElement extends LitElement {
  static override styles = [
    shoelaceStyles,
    smartmapsAutocompleteStyles,
    styles,
    css`
      :host {
        display: block;
        margin: 2em 0;
      }

      ::part(form-control-label) {
        font-size: 105%;
        font-weight: bold;
        font-family: var(--ca-font-headline);
        margin: 0;
        height: 35px;
      }

      form {
        background: var(--sl-color-neutral-0);
        padding: 2em;
        display: flex;
        flex-direction: column;
        gap: 1em;
      }

      form > div {
        display: flex;
        flex-wrap: wrap;
        gap: 2em 1em;
        justify-content: space-between;
      }

      form > div > div {
        display: flex;
        flex-wrap: wrap;
        gap: 1em;
      }

      [name="location"] {
      }

      [name="radius"] {
      }

      [type="submit"] {
        align-content: flex-end;
      }

      .sm-autocomplete {
        display: block;
        padding: 0;
        margin: 0;
        background: none;
        font-size: inherit;
        color: inherit;
      }

      .sm-autocomplete .autocomplete-search-clear-button.visible {
        display: none !important;
      }

      .sm-autocomplete [type="search"], .sm-autocomplete [type="text"] {
        background-color: transparent;
      }

      sl-button::part(base) {
        transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
      }

      sl-button::part(base):hover {
        background-color: var(--ca-color-primary);
        color: var(--sl-color-neutral-0-3rd);
      }

      sl-button::part(base):active {
        background-color: var(--sl-color-neutral-0-2nd);
        color: var(--sl-input-label-color);
      }

      sl-button::part(base) .h3 {
        margin: 0;
      }

      sl-input {
        display: inline-block;
      }
    `,
  ];

  private fulltextRef: Ref<HTMLInputElement> = createRef();
  private locationRef: Ref<HTMLInputElement> = createRef();
  private radiusRef: Ref<HTMLInputElement> = createRef();
  private _controller!: SearchController;

  override connectedCallback() {
    log(this.constructor.name, 'connectedCallback');
    super.connectedCallback();

    this._controller = SearchController.instance(this);
  }

  override firstUpdated() {
    createAutocomplete(
      this.locationRef.value!,
      ConfigurationState.configuration.smartmapsAccessToken,
      {
        showCountry: false,
        showState: false,
        showCounty: false,
        showDistrict: true,
        showZip: true,
        showCityPart: true,
        showVillage: false,
        showNeighborhood: false,
        showStreet: false,
        showPoi: false,
        proximityBoost: {
          enable: true,
          value: 10,
          radius: 200,
        },
        searchZip: true,
        searchIconPosition: 'none',
        enableSearchOnClick: true,
      },
    ).then((autocomplete) => {
      autocomplete.options.isoCountries = ['de'];
      autocomplete.addEventListener('selected', (event: CustomEvent) => {
        log(this.constructor.name, 'Selected', event);
        const feature: Feature | undefined = event.detail.geojson;
        if (feature === undefined) {
          log(this.constructor.name, 'Selected', 'Not a feature');
          return;
        }

        this.locationRef.value!.value = feature.properties?.displayValue;
        this._controller.searchObject.lat = (<Point>feature.geometry).coordinates[1].toString();
        this._controller.searchObject.lng = (<Point>feature.geometry).coordinates[0].toString();

        this.dispatchEvent(
          new CustomEvent(
            'ca-map-feature-requested',
            {detail: feature},
          ),
        );

        if (this.radiusRef.value!.value === '') {
          this._controller.searchObject.radiusKm = this.radiusRef.value!.value = '15';
        }

        this._controller.searchWasManuallyTriggered = true;
        this._controller.fetch('geoJson');
      });
      autocomplete.addEventListener('suggestions', (event: CustomEvent) => {
        log(this.constructor.name, 'Suggestions', event);
      });
      autocomplete.addEventListener('empty', (event: CustomEvent) => {
        log(this.constructor.name, 'Empty', event);
      });
    });
  }

  override render() {
    return html`
      <form @submit=${this._handleSubmit}>
        <multi-input ${ref(this.fulltextRef)}
                     @change=${(ev: Event) => {
      log(this.constructor.name, 'multi-input @change', ev);
      this._controller.searchObject.fullText = this.fulltextRef.value!.value;
      this._controller.fetch();
    }}
        >
          <sl-input
            placeholder="${ConfigurationState.configuration.fulltextPlaceholder}"
          >
            <label slot="label">
              <ca-overall-count></ca-overall-count>
              Einträge im ${ConfigurationState.configuration.title} durchsuchen:
            </label>
          </sl-input>
        </multi-input>

        <div>
          <div>
            <sl-input ${ref(this.locationRef)}
                      label="Wo?"
                      name="location"
                      placeholder="Ort oder PLZ"
            ></sl-input>

            <sl-select ${ref(this.radiusRef)}
                       @sl-change=${(ev: Event) => {
      this._controller.searchObject.radiusKm = this.radiusRef.value!.value;
      this._controller.updated();
    }}
                       label="Umkreis"
                       name="radius"
            >
              <sl-option value=""></sl-option>
              <sl-option value="5">5km</sl-option>
              <sl-option value="10">10km</sl-option>
              <sl-option value="15">15km</sl-option>
              <sl-option value="30">30km</sl-option>
            </sl-select>
          </div>

          <sl-button type="submit" size="large">
            <div class="h3">
              ${ConfigurationState.configuration.featureName} finden
            </div>
          </sl-button>
        </div>
      </form>
    `;
  }

  private _handleSubmit(ev: Event) {
    log(this.constructor.name, '_handleSubmit');
    ev.preventDefault();
    this._controller.searchObject.fullText = this.fulltextRef.value!.value;
    this._controller.searchWasManuallyTriggered = true;
    this._controller.fetch('geoJson');
  }
}
